import { Stack } from '@ardoq/layout';
import { DragAndDropSectionsProvider } from '@ardoq/drag-and-drop';
import { type I18nLocale, t } from '@ardoq/i18n';
import { Group } from './PerspectiveGroup';
import {
  AddPerspectiveGroupButton,
  DisabledOptionProps,
  GetInvalidGroupingSelectOptions,
  GetRuleNotificationParams,
  PerspectiveGroupingRule,
  PerspectivesGroupsOptions,
  TabNotification,
  NotificationData,
  GetGroupDisabledProps,
} from '@ardoq/perspectives';

const getDisabledOptionProps = (
  i18nLocale: I18nLocale
): DisabledOptionProps => ({
  getAddButtonDisabledPopoverContent: entitiesName =>
    t(
      i18nLocale,
      'perspectivesSidebar:labels.noEntitiesAvailable',
      { entitiesName },
      `There are no ${entitiesName} in the opened datasets. Try editing your datasets or open more data.`
    ),
});

type PerspectivesGroupsProps = {
  groupingOptions: PerspectivesGroupsOptions;
  groupingRules: PerspectiveGroupingRule[];
  getRuleNotificationParams?: GetRuleNotificationParams;
  onGroupingRuleReordered: (
    reorderedGroupingRules: PerspectiveGroupingRule[]
  ) => void;
  onGroupingRuleDeleted: (groupingRuleId: string) => void;
  onGroupingRuleAdded: (newGroupingRule: PerspectiveGroupingRule) => void;
  onGroupingRuleUpdated: (
    groupingRuleId: string,
    updatedGroupingRule: Partial<PerspectiveGroupingRule>
  ) => void;
  getInvalidGroupingSelectOptions?: GetInvalidGroupingSelectOptions;
  restrictedGroupFormattingWarningProps?: NotificationData;
  disableAddPerspectiveGroupButton?: boolean;
  getGroupDisabledProps?: GetGroupDisabledProps;
  isGroupingByTypeNamesEnabled?: boolean;
  i18nLocale: I18nLocale;
};

export const PerspectivesGroups = ({
  groupingOptions,
  groupingRules,
  onGroupingRuleReordered,
  onGroupingRuleDeleted,
  onGroupingRuleAdded,
  onGroupingRuleUpdated,
  getRuleNotificationParams,
  getInvalidGroupingSelectOptions,
  restrictedGroupFormattingWarningProps,
  disableAddPerspectiveGroupButton,
  getGroupDisabledProps,
  isGroupingByTypeNamesEnabled,
  i18nLocale,
}: PerspectivesGroupsProps) => {
  const groupRulesIndexedById = Object.fromEntries(
    groupingRules.map(group => [group.id, group])
  );

  const reorderGroupings = (reorderedGroupingRulesIds: string[]) => {
    const reorderedGroupingRules = reorderedGroupingRulesIds.map(
      id => groupRulesIndexedById[id]
    );
    onGroupingRuleReordered(reorderedGroupingRules);
  };

  return (
    <Stack gap="medium">
      <TabNotification
        notificationData={restrictedGroupFormattingWarningProps}
      />
      <DragAndDropSectionsProvider onDrop={reorderGroupings}>
        {groupingRules.map(groupingRule => (
          <Group
            onGroupingRuleDeleted={onGroupingRuleDeleted}
            onGroupingRuleUpdated={onGroupingRuleUpdated}
            dragAndDropCardId={groupingRule.id}
            groupingOptions={groupingOptions}
            key={groupingRule.id}
            groupingRule={groupingRule}
            getRuleNotificationParams={getRuleNotificationParams}
            getInvalidGroupingSelectOptions={getInvalidGroupingSelectOptions}
            isGroupingByTypeNamesEnabled={isGroupingByTypeNamesEnabled}
            i18nLocale={i18nLocale}
            {...getGroupDisabledProps?.(groupingRule)}
          />
        ))}
      </DragAndDropSectionsProvider>
      <div>
        <AddPerspectiveGroupButton
          groupingOptions={groupingOptions}
          onGroupingRuleAdded={onGroupingRuleAdded}
          disabledOptionProps={getDisabledOptionProps(i18nLocale)}
          isDisabled={disableAddPerspectiveGroupButton}
          isGroupingByTypeNamesEnabled={isGroupingByTypeNamesEnabled}
          i18nLocale={i18nLocale}
        />
      </div>
    </Stack>
  );
};
